import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Linkedin, Mail } from "lucide-react";
import BrandLockup from "@/components/BrandLockup";
import { useLang } from "@/lib/i18n";

const CONTACT_EMAIL = "hello@viajadeverdad.com";
const INSTAGRAM = "https://www.instagram.com/viajadeverdad/";
const LINKEDIN = "https://www.linkedin.com/company/viaja-de-verdad/";

export default function Footer() {
  const { t } = useLang();
  // TODO: Re-enable when catalog > 30 experiences (mirrors SHOW_COLLECTION_NAV
  // in Header.jsx). Route `/the-collection` stays mounted so bookmarks work.
  const SHOW_COLLECTION_NAV = false;
  const navItems = [
    { to: "/spain", label: t.nav.spain },
    { to: "/italy", label: t.nav.italy },
    { to: "/portugal", label: t.nav.portugal },
    { to: "/experiences", label: t.nav.experiences },
    ...(SHOW_COLLECTION_NAV
      ? [{ to: "/the-collection", label: t.nav.collection }]
      : []),
    { to: "/contact", label: t.nav.contact },
  ];
  const legalItems = [
    { label: t.footer.legal_privacy, to: "/privacy" },
    { label: t.footer.legal_cookies, to: "/cookies" },
    { label: t.footer.legal_legal, to: "/legal" },
  ];
  return (
    <footer data-testid="site-footer" className="relative bg-charcoal text-ivory">
      <div className="absolute inset-x-0 top-0 h-px bg-brass/60" aria-hidden />
      <div className="mx-auto max-w-7xl px-6 md:px-10 pt-20 pb-10">
        <div className="flex justify-center">
          <Link to="/" aria-label="Viaja de Verdad" className="inline-block">
            <BrandLockup size="footer" tone="dark" />
          </Link>
        </div>

        <div className="mt-16 md:mt-20 mx-auto h-px bg-ivory/15" style={{ maxWidth: 720 }} aria-hidden />

        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mt-14 md:mt-16">
          <div className="md:col-span-5">
            <h3 className="eyebrow text-brass mb-5">{t.footer.studio_eyebrow}</h3>
            <p className="italic-quote text-lg text-ivory/80 leading-relaxed max-w-sm">
              {t.footer.tagline}
            </p>
            <p className="text-sm text-ivory/55 mt-5 leading-relaxed max-w-sm">
              {t.footer.founded}
            </p>
          </div>

          <div className="md:col-span-3">
            <h3 className="eyebrow text-brass mb-5">{t.footer.navigate_eyebrow}</h3>
            <ul className="space-y-3">
              {navItems.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className="text-base text-ivory/85 hover:text-ivory link-underline">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <h3 className="eyebrow text-brass mb-5">{t.footer.contact_eyebrow}</h3>
            <p className="text-sm text-ivory/65 mb-3">{t.footer.reach_us}</p>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="inline-flex items-center gap-3 text-ivory hover:text-ivory link-underline italic-quote text-lg"
              data-testid="footer-email"
            >
              <Mail size={16} strokeWidth={1.5} />
              {CONTACT_EMAIL}
            </a>
            <ul className="flex items-center gap-5 mt-7">
              <li>
                <a href={INSTAGRAM} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-ivory/85 hover:text-ivory link-underline">
                  <Instagram size={16} strokeWidth={1.5} />
                  <span className="text-sm">Instagram</span>
                </a>
              </li>
              <li>
                <a href={LINKEDIN} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-ivory/85 hover:text-ivory link-underline">
                  <Linkedin size={16} strokeWidth={1.5} />
                  <span className="text-sm">LinkedIn</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-ivory/15 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="text-xs text-ivory/55 tracking-wide">
            © {new Date().getFullYear()} Viaja de Verdad — Singular Experiences
          </p>
          <ul className="flex items-center gap-6">
            {legalItems.map((item) => (
              <li key={item.to}>
                <Link to={item.to} className="text-xs text-ivory/55 hover:text-ivory uppercase tracking-[0.2em]">
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  );
}
